import CornerstoneLogo from '@/assets/logos/CornerstoneLogo';
import LoginStep from '@/components/PageSpecific/login/LoginStep';
import MfaStep from '@/components/PageSpecific/login/MfaStep';
import NewPasswordStep from '@/components/PageSpecific/login/NewPasswordStep';
import PrivacyPolicyDialog from '@/components/PageSpecific/privacy-policy/PrivacyPolicyDialog';
import TermsOfUseDialog from '@/components/PageSpecific/terms-of-use/TermsOfUseDialog';
import SnackBarAlert from '@/components/Reusable/GeneralUse/SnackBarAlert';
import PageTitle from '@/components/Reusable/Typography/PageTitle';
import classes from '@/styles/login.module.css';
import { hubThemeLight } from '@/ui/theme';
import { CustomThemeProvider } from '@/ui/theme/ThemeContext';
import { redirectAuthenticatedUser } from '@/utils/auth/redirectAuthenticatedUser';
import useLogin from '@/utils/hooks/useLogin';
import useMfaFlow from '@/utils/hooks/useMfaFlow';
import useNewPassword from '@/utils/hooks/useNewPassword';
import { Box, Grid, Paper } from '@mui/material';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

function Login() {
  const router = useRouter();
  const [openTOU, setOpenTOU] = useState(false);
  const [openPP, setOpenPP] = useState(false);
  const [openSnack, setOpenSnack] = useState(false);
  const [alert, setAlert] = useState(undefined);
  const [snackPack, setSnackPack] = useState([]);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    newPassword: '',
  });

  const updateSnack = (alertObj) => {
    setSnackPack((prev) => [
      ...prev,
      { ...alertObj, key: new Date().getTime() },
    ]);
  };

  useEffect(() => {
    if (!router.isReady) return;

    redirectAuthenticatedUser(router);
  }, [router]);

  const {
    loadingStep,
    mfaUser,
    setMfaUser,
    cognitoResponse,
    finalizingNewPassword,
    setFinalizingNewPassword,
    step,
    setStep,
    handleLoginSubmit,
  } = useLogin(formData, updateSnack);

  const { loading: newPasswordLoading, handleNewPasswordSubmit } =
    useNewPassword(formData, cognitoResponse, updateSnack);

  const {
    mfa,
    loadingStep: mfaLoadingStep,
    handleMfaMethodContinue,
    handleOtpSubmit,
    handleResendOtp,
    handleSelectDifferentMethod,
  } = useMfaFlow(mfaUser, setMfaUser, setStep, updateSnack);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const clearFormData = () => {
    setFormData({
      email: '',
      password: '',
      newPassword: '',
    });
  };

  const handleLoginSubmitWrapper = async () => {
    const redirected = await redirectAuthenticatedUser(router);
    if (redirected) {
      return;
    }

    const result = await handleLoginSubmit();
    // Clear form data on successful login (not for MFA or new password flows)
    if (
      result?.success &&
      !result?.requiresMfa &&
      !result?.requiresNewPassword
    ) {
      clearFormData();
    }
  };

  const handleKeycloakLogin = async () => {
    const redirected = await redirectAuthenticatedUser(router);
    if (redirected) {
      return;
    }

    router.push('/sso?sso_provider=keycloak');
  };

  const handleOtpSubmitWrapper = async ({ otp }) => {
    await handleOtpSubmit({ otp });
    clearFormData();
  };

  const handleNewPasswordSubmitWrapper = async () => {
    const result = await handleNewPasswordSubmit();

    // If password was set successfully, reset state to show login form
    if (result?.success) {
      setFinalizingNewPassword(false);
      setStep('login');
      setMfaUser(null);
      // Clear form data
      setFormData({
        email: '',
        password: '',
        newPassword: '',
      });
    }

    // Check if MFA is required after password completion (shouldn't happen with current flow)
    if (result?.requiresMfa && result?.mfaUser) {
      setMfaUser(result.mfaUser);
      setFinalizingNewPassword(false);
      setStep('mfa-method');
    }
  };

  return (
    <CustomThemeProvider initialSettings={{ themeOverride: hubThemeLight }}>
      <Grid
        container
        spacing={0}
        direction='column'
        alignItems='center'
        style={{
          backgroundImage: `url("${process.env.NEXT_PUBLIC_STATIC_ASSETS_URL}cis-hub-bg.jpg")`,
        }}
        className={classes.wrapper}
      >
        <PageTitle />
        <Box
          m='auto'
          width={'80%'}
          maxWidth={450}
          display='flex'
          flexDirection='column'
          alignItems='center'
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: 400,
              display: 'flex',
              marginBottom: 3,
            }}
          >
            <CornerstoneLogo white />
          </Box>

          <Box
            component={Paper}
            width={'100%'}
            bgcolor='white'
            justifyContent='center'
            display='flex'
            flexDirection='column'
            elevation={12}
            className={classes.paper}
            p={1}
          >
            {finalizingNewPassword ? (
              <NewPasswordStep
                formData={formData}
                handleInputChange={handleInputChange}
                loading={newPasswordLoading}
                handleNewPasswordSubmit={handleNewPasswordSubmitWrapper}
              />
            ) : step === 'login' ? (
              <LoginStep
                formData={formData}
                handleInputChange={handleInputChange}
                loading={loadingStep === 'login'}
                handleLoginSubmit={handleLoginSubmitWrapper}
                handleKeycloakLogin={handleKeycloakLogin}
                onOpenPP={() => setOpenPP(true)}
                onOpenTOU={() => setOpenTOU(true)}
              />
            ) : (
              <MfaStep
                step={step}
                mfa={mfa}
                loadingStep={mfaLoadingStep}
                onMfaMethodContinue={handleMfaMethodContinue}
                onOtpSubmit={handleOtpSubmitWrapper}
                onResendOtp={handleResendOtp}
                onSelectDifferentMethod={handleSelectDifferentMethod}
              />
            )}
          </Box>
        </Box>
        <TermsOfUseDialog open={openTOU} setOpen={setOpenTOU} />
        <PrivacyPolicyDialog open={openPP} setOpen={setOpenPP} />
        <SnackBarAlert
          openSnack={openSnack}
          setOpenSnack={setOpenSnack}
          alert={alert}
          setAlert={setAlert}
          snackPack={snackPack}
          setSnackPack={setSnackPack}
        />
      </Grid>
    </CustomThemeProvider>
  );
}

export default Login;
